import { InterviewRowTable, type HiddenRowsNotice, type RowTableColumn, type RowTableRow } from "./InterviewRowTable";
import { SharedRowNotes } from "./ConsequencePicker";

/**
 * s221 Lane 1, mechanism 3 — A SECOND TABLE PER TILE (SPEC: `second: { tiles, cols, headers, cells }`).
 *
 * Some tiles on one screen need different columns: the children table asks for a guardian named in
 * the will, the dependants table asks who looks after them. The SPEC splits the picked rows by tile —
 * rows from the tiles named in `second.tiles` go to the second table, every other row to the first —
 * and draws the two tables one under the other. This is that split, and nothing more: both tables
 * are `InterviewRowTable`, so a row behaves the same whichever table it lands in.
 *
 * The rows are still ONE list in the screen (and, for children and dependants, one section with a
 * kind column — D-221.10); the split is how they are drawn, not how they are stored.
 */

export type TiledRow = RowTableRow & { tileId: string };

export type TableSpec = {
  columns: readonly RowTableColumn[];
  gridTemplate: string;
  rowsLead?: string;
  rowsNote?: string;
  /** s221 Lane 1d: rows hidden on an earlier visit, and how to bring them back. */
  hiddenNotice?: HiddenRowsNotice;
};

/** Split rows between the two tables. Pure; row order is kept within each table. */
export function splitRowsByTile<R extends { tileId: string }>(rows: readonly R[], secondTiles: readonly string[]): { first: R[]; second: R[] } {
  const first: R[] = [];
  const second: R[] = [];
  for (const r of rows) (secondTiles.includes(r.tileId) ? second : first).push(r);
  return { first, second };
}

export function TileTables({
  rows,
  first,
  second,
  onAddRow,
  onRemove,
  testIdPrefix = "interview-tables",
  cutLead = false,
  rowMarks = false,
  strongRowLine = false,
  firstNotes = [],
  secondNotes = [],
}: {
  rows: readonly TiledRow[];
  first: TableSpec;
  /** Omit when the screen has one table; every row then goes to `first`. */
  second?: TableSpec & { tiles: readonly string[] };
  onAddRow?: (rowId: string) => void;
  onRemove: (rowId: string) => void;
  testIdPrefix?: string;
  /** s227 (D14): both tables clamp the name and open a cut one in full (InterviewRowTable `cutLead`). */
  cutLead?: boolean;
  /** s233 (D-233.75, D-233.70(7)): each row's mark in its own column, and the #A89F8D row line — on for chapter one. */
  rowMarks?: boolean;
  strongRowLine?: boolean;
  /** s234 (D-234.94): a note identical on several rows of a table, drawn once after that table, naming its rows. */
  firstNotes?: readonly { text: string }[];
  secondNotes?: readonly { text: string }[];
}) {
  const split = splitRowsByTile(rows, second?.tiles ?? []);
  // The screen's own prefix ("interview-kids-tables" -> "interview-kids"), so a shared note reads as the screen's.
  const sharedNotePrefix = testIdPrefix.replace(/-tables$/, "");
  return (
    <div data-testid={testIdPrefix}>
      <InterviewRowTable {...first} hiddenNotice={first.hiddenNotice} rows={split.first} onAddRow={onAddRow} onRemove={onRemove} testIdPrefix={`${testIdPrefix}-first`} cutLead={cutLead} rowMarks={rowMarks} strongRowLine={strongRowLine} />
      <SharedRowNotes notes={firstNotes} testIdPrefix={sharedNotePrefix} />
      {second && (
        <InterviewRowTable columns={second.columns} gridTemplate={second.gridTemplate} rowsLead={second.rowsLead} rowsNote={second.rowsNote} hiddenNotice={second.hiddenNotice}
          rows={split.second} onAddRow={onAddRow} onRemove={onRemove} testIdPrefix={`${testIdPrefix}-second`} cutLead={cutLead} rowMarks={rowMarks} strongRowLine={strongRowLine} />
      )}
      {second && <SharedRowNotes notes={secondNotes} testIdPrefix={`${sharedNotePrefix}-second`} />}
    </div>
  );
}
